"use client";

import React, { useState, useEffect, useMemo } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Calendar as CalendarIcon, Phone, Mail, User, Send, CircleCheck, Navigation, X, Check, Loader as Loader2, CircleAlert as AlertCircle, Info, MessageCircle, RefreshCw } from 'lucide-react';
import { cn } from "@/lib/utils";
const NEWS_CATEGORIES = ["Kriminal & Hukum", "Politik & Pemerintahan", "Ekonomi & Bisnis", "Sosial & Budaya", "Pendidikan", "Kesehatan", "Olahraga", "Teknologi", "Hiburan & Gaya Hidup", "Wisata & Kuliner", "Bencana Alam", "Lalu Lintas", "Opini Publik", "Lainnya"];
const SENDER_STATUS_OPTIONS = ["Masyarakat Umum", "Anggota PP", "Ojol", "Tokoh Masyarakat", "Pejabat Publik", "Aktivis", "Mahasiswa", "Lainnya"];
const DATE_OPTIONS = Array.from({
  length: 14
}, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - 7 + i);
  return {
    label: d.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }),
    value: d.toISOString()
  };
});
const ZONA_DATA = ['Zona 1 (Pusat Kota)', 'Zona 2 (Utara)', 'Zona 3 (Timur Laut)', 'Zona 4 (Timur)', 'Zona 5 (Tenggara)', 'Zona 6 (Selatan)', 'Zona 7 (Barat Daya Pesisir)'];
const ZONA_KECAMATAN_MAP: Record<string, string> = {
  "Zona 1 (Pusat Kota)": "Garut Kota, Karangpawitan, Sucinaraja, Wanaraja, Cibatu, Pangatikan, Sukawening, Karangtengah",
  "Zona 2 (Utara)": "Banyuresmi, Leuwigoong, Cibiuk, Balubur Limbangan, Selaawi, Malangbong, Kersamanah",
  "Zona 3 (Timur Laut)": "Tarogong Kaler, Tarogong Kidul, Leles, Kadungora, Samarang, Pasirwangi, Sukaresmi",
  "Zona 4 (Timur)": "Cisurupan, Cigedug, Cikajang, Cilawu, Bayongbong",
  "Zona 5 (Tenggara)": "Singajaya, Peundeuy, Cihurip, Banjarwangi",
  "Zona 6 (Selatan)": "Bungbulang, Pamulihan, Pakenjeng, Talegong, Cisewu",
  "Zona 7 (Barat Daya Pesisir)": "Caringin, Cibalong, Pameungpeuk, Cikelet, Cisompet, Mekarmukti"
};
function validateFormCompletion(formData: any) {
  const missingItems: string[] = [];
  let progress = 0;
  const hasGps = formData.gpsActive && formData.gpsLatitude && formData.gpsLongitude;
  const hasLocation = formData.wilayah && formData.kecamatan && formData.village;
  if (hasGps && hasLocation) progress += 20;else {
    if (!formData.gpsActive) missingItems.push("Aktivasi GPS");
    if (!hasLocation) missingItems.push("Lokasi (Wilayah, Kecamatan, Desa/Kelurahan) wajib diisi");
  }
  const has5W1H = formData.what && formData.who && formData.when && formData.where && formData.why && formData.how?.length >= 300;
  if (has5W1H) progress += 20;else {
    if (!formData.what) missingItems.push("What (Apa yang terjadi)");
    if (!formData.who) missingItems.push("Who (Siapa yang terlibat)");
    if (!formData.when) missingItems.push("When (Kapan kejadian)");
    if (!formData.where) missingItems.push("Where (Lokasi detail)");
    if (!formData.why) missingItems.push("Why (Penyebab)");
    if (!formData.how || formData.how.length < 300) missingItems.push("How (Kronologi min. 300 karakter)");
  }
  const hasCatPri = formData.category && formData.priority;
  if (hasCatPri) progress += 20;else {
    if (!formData.category) missingItems.push("Kategori Berita");
    if (!formData.priority) missingItems.push("Tingkat Prioritas");
  }
  const hasSender = formData.senderStatus && formData.senderName && formData.senderPhone && formData.senderAddress;
  if (hasSender) progress += 20;else {
    if (!formData.senderName) missingItems.push("Nama Lengkap Pengirim");
    if (!formData.senderPhone) missingItems.push("Nomor WhatsApp Pengirim");
    if (!formData.senderAddress) missingItems.push("Alamat Detail Pengirim");
  }
  if (formData.allAgreementsSigned) progress += 20;else missingItems.push("Persetujuan Pedoman Media Siber");
  return {
    isValid: progress === 100,
    progress,
    missingItems,
    details: {
      hasGps,
      has5W1H,
      hasCatPri,
      hasSender
    }
  };
}
function SubmissionSuccessModal({
  refId,
  postId,
  whatsappStatusAdmin,
  whatsappStatusPengirim,
  onReset
}: any) {
  const handleWhatsAppConfirmation = () => {
    const message = `Halo Redaksi ABADI.NEWS, saya ingin mengonfirmasi laporan informasi kejadian dengan nomor referensi: ${refId}`;
    window.open(`https://wa.me/6281234567890?text=${encodeURIComponent(message)}`, '_blank');
  };
  return <div className="p-8 text-center space-y-6 animate-in fade-in zoom-in-95 duration-500">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-lg bg-green-100 text-green-600 mb-2">
        <Check className="size-10" />
      </div>
      <div className="space-y-2">
        <h3 className="text-2xl font-bold text-foreground">Laporan Berhasil Terkirim!</h3>
        <p className="text-muted-foreground">Terima kasih atas kontribusi Anda. Laporan Anda sedang dalam antrean verifikasi redaksi.</p>
      </div>

      <div className="bg-muted/50 p-6 rounded-lg border border-border space-y-4">
        <div className="space-y-1">
          <span className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Nomor Referensi</span>
          <div className="font-mono text-xl font-bold text-primary select-all">
            {refId}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
          <div className="text-left space-y-1">
            <span className="text-[10px] font-bold uppercase text-muted-foreground">Status Admin</span>
            <div className={cn("text-xs font-bold px-2 py-1 rounded-md inline-block", whatsappStatusAdmin === 'sent' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>
              {whatsappStatusAdmin === 'sent' ? 'NOTIFIKASI TERKIRIM' : 'PENDING'}
            </div>
          </div>
          <div className="text-left space-y-1">
            <span className="text-[10px] font-bold uppercase text-muted-foreground">Status Pengirim</span>
            <div className={cn("text-xs font-bold px-2 py-1 rounded-md inline-block", whatsappStatusPengirim === 'sent' ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>
              {whatsappStatusPengirim === 'sent' ? 'NOTIFIKASI TERKIRIM' : 'PENDING'}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Button onClick={handleWhatsAppConfirmation} variant="secondary" className="w-full h-[54px] rounded-lg font-bold flex items-center justify-center gap-2 shadow-md">
          <MessageCircle className="size-5" />
          KONFIRMASI VIA WHATSAPP
        </Button>
        <Button onClick={onReset} variant="outline" className="w-full h-[54px] rounded-lg font-bold">
          KIRIM BERITA LAGI
        </Button>
      </div>
      
      <p className="text-xs text-muted-foreground italic">
        *Simpan nomor referensi di atas untuk keperluan pelacakan status berita Anda di masa mendatang.
      </p>
    </div>;
}
function InnerForm() {
  const {
    control,
    watch,
    setValue,
    resetForm
  } = useWvcForm();
  const [refId, setRefId] = useState<string | null>(null);
  const [postId, setPostId] = useState<number | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [whatsappStatusAdmin, setWhatsappStatusAdmin] = useState<string>('pending');
  const [whatsappStatusPengirim, setWhatsappStatusPengirim] = useState<string>('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [kecamatanOptions, setKecamatanOptions] = useState<{
    id: number;
    name: string;
  }[]>([]);
  const [isGpsLoading, setIsGpsLoading] = useState(false);
  const senderStatus = watch("senderStatus");
  const channel = watch("channel");
  const gpsActive = watch("gpsActive");
  const selectedWilayah = watch("wilayah");
  const gpsLatitude = watch("gpsLatitude");
  const gpsLongitude = watch("gpsLongitude");
  const wilayah = watch("wilayah");
  const kecamatan = watch("kecamatan");
  const village = watch("village");
  const category = watch("category");
  const priority = watch("priority");
  const senderName = watch("senderName");
  const senderPhone = watch("senderPhone");
  const senderAddress = watch("senderAddress");
  const what = watch("what");
  const who = watch("who");
  const when = watch("when");
  const where = watch("where");
  const why = watch("why");
  const how = watch("how");
  const allAgreementsSigned = watch("allAgreementsSigned");
  const validation = useMemo(() => validateFormCompletion({
    gpsActive,
    gpsLatitude,
    gpsLongitude,
    wilayah,
    kecamatan,
    village,
    category,
    priority,
    senderStatus,
    senderName,
    senderPhone,
    senderAddress,
    what,
    who,
    when,
    where,
    why,
    how,
    allAgreementsSigned
  }), [gpsActive, gpsLatitude, gpsLongitude, wilayah, kecamatan, village, category, priority, senderStatus, senderName, senderPhone, senderAddress, what, who, when, where, why, how, allAgreementsSigned]);
  useEffect(() => {
    if (!selectedWilayah) {
      setKecamatanOptions([]);
      return;
    }
    const kecamatanString = ZONA_KECAMATAN_MAP[selectedWilayah];
    if (kecamatanString) {
      const options = kecamatanString.split(", ").map((name, index) => ({
        id: index + 1,
        name: name
      }));
      setKecamatanOptions(options);
      setValue("kecamatan", "");
      setValue("zona_id", selectedWilayah);
    } else {
      setKecamatanOptions([]);
    }
  }, [selectedWilayah, setValue]);
  const submitToAPI = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      const formData = new FormData();
      const allValues = watch();
      Object.keys(allValues).forEach(key => {
        if (allValues[key] !== undefined && allValues[key] !== null) {
          formData.append(key, allValues[key].toString());
        }
      });
      formData.append('zona', allValues.wilayah);
      formData.append('kecamatan', allValues.kecamatan);
      formData.append('desa_kelurahan', allValues.village);
      const photos = watch("photos") || [];
      const videos = watch("videos") || [];
      const documents = watch("documents") || [];
      photos.forEach((file: File) => formData.append('photos[]', file));
      videos.forEach((file: File) => formData.append('videos[]', file));
      documents.forEach((file: File) => formData.append('documents[]', file));
      const response = await fetch('/wp-json/abadi/v1/kirim-berita', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        setRefId(result.data.ref_id);
        setPostId(result.data.post_id);
        setWhatsappStatusAdmin(result.data.whatsapp_status_admin);
        setWhatsappStatusPengirim(result.data.whatsapp_status_pengirim);
        setSubmissionStatus('success');
      } else {
        throw new Error(result.message || "Gagal mengirim laporan. Silakan coba lagi.");
      }
    } catch (error: any) {
      setSubmissionError(error.message || "Terjadi kesalahan koneksi. Silakan coba lagi.");
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validation.isValid) {
      submitToAPI();
    }
  };
  const handleReset = () => {
    resetForm();
    setRefId(null);
    setPostId(null);
    setSubmissionStatus('pending');
    setSubmissionError(null);
  };
  if (submissionStatus === 'success' && refId) {
    return <SubmissionSuccessModal refId={refId} postId={postId} whatsappStatusAdmin={whatsappStatusAdmin} whatsappStatusPengirim={whatsappStatusPengirim} onReset={handleReset} />;
  }
  const inputClassName = "h-[54px] border-[#EEEEEE] rounded-lg focus-visible:ring-0 focus-visible:border-[#FF6B00] text-base bg-white";
  const handleGpsActivation = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation tidak didukung oleh browser Anda.");
      return;
    }
    setIsGpsLoading(true);
    navigator.geolocation.getCurrentPosition(async position => {
      const {
        latitude,
        longitude
      } = position.coords;
      setValue("gpsLatitude", latitude.toString());
      setValue("gpsLongitude", longitude.toString());
      setValue("mapsLink", `https://www.google.com/maps?q=${latitude},${longitude}`);
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`);
        const data = await response.json();
        if (data && data.address) {
          const addr = data.address;
          setValue("address", data.display_name || "Lokasi terdeteksi GPS");
          setValue("district", addr.city_district || addr.county || "");
          setValue("city", addr.city || addr.town || addr.municipality || "");
          setValue("province", addr.state || "");
          setValue("postalCode", addr.postcode || "");
          setValue("gpsActive", true);
          setValue("where", data.display_name || "");
        } else {
          setValue("address", "Lokasi terdeteksi GPS");
          setValue("gpsActive", true);
        }
      } catch (error) {
        console.error("Error fetching address:", error);
        setValue("address", "Lokasi terdeteksi GPS");
        setValue("gpsActive", true);
      } finally {
        setIsGpsLoading(false);
      }
    }, error => {
      console.error("Geolocation error:", error);
      alert("Gagal mengambil lokasi. Pastikan izin lokasi telah diberikan.");
      setIsGpsLoading(false);
    }, {
      enableHighAccuracy: true,
      timeout: 10000
    });
  };
  return <Form onSubmit={onSubmit}>
      <div className="space-y-10">
        {submissionError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center justify-between gap-4 animate-in slide-in-from-top-2">
            <div className="flex items-center gap-3">
              <AlertCircle className="size-5 text-destructive" />
              <p className="text-destructive text-sm font-medium">{submissionError}</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => submitToAPI()} className="h-8 text-xs font-bold border-destructive/20 hover:bg-destructive/5">
              <RefreshCw className="size-3 mr-1" /> COBA LAGI
            </Button>
          </div>}

        <Card className="border-[#EEEEEE] shadow-sm rounded-lg">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Navigation className="size-5 text-primary" />
                  Layanan Lokasi Presisi
                </h3>
                <p className="text-sm text-muted-foreground">Aktifkan GPS untuk memverifikasi lokasi kejadian secara akurat.</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Button type="button" onClick={handleGpsActivation} disabled={isGpsLoading || isSubmitting} className={cn("h-[54px] px-8 rounded-lg font-bold transition-all", gpsActive ? "bg-green-600 hover:bg-green-700 text-white" : "bg-destructive hover:bg-destructive/90 text-white", (isGpsLoading || isSubmitting) && "opacity-80 cursor-not-allowed")}>
                  {isGpsLoading ? <><Loader2 className="mr-2 size-5 animate-spin" /> MENGAMBIL LOKASI...</> : gpsActive ? <><CircleCheck className="mr-2 size-5" /> GPS AKTIF</> : <><AlertCircle className="mr-2 size-5" /> AKTIFKAN GPS</>}
                </Button>
                {!gpsActive && <span className="text-[10px] text-destructive font-bold animate-pulse">GPS wajib aktif sebelum melanjutkan</span>}
              </div>
            </div>

            {gpsActive && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#EEEEEE] animate-in fade-in slide-in-from-top-2">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold uppercase text-muted-foreground">Alamat Lengkap</label>
                  <Input placeholder="Alamat lokasi kejadian" value={watch("address")} onChange={e => setValue("address", e.target.value)} className="bg-white border-[#EEEEEE] h-10" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FormField control={control} name="village" rules={{
                required: "Desa/Kelurahan wajib diisi"
              }} render={({
                field
              }) => <FormItem className="space-y-2">
                        <FormLabel className="text-xs font-bold uppercase text-muted-foreground">Desa/Kel</FormLabel>
                        <FormControl>
                          <Input placeholder="Ketik nama Desa atau Kelurahan" className="bg-white border-[#EEEEEE] h-10" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground">Kecamatan</label>
                    <Input readOnly value={watch("district")} className="bg-muted/30 border-none h-10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground">Kota/Kab</label>
                    <Input readOnly value={watch("city")} className="bg-muted/30 border-none h-10" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground">Provinsi</label>
                    <Input readOnly value={watch("province")} className="bg-muted/30 border-none h-10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground">Kode Pos</label>
                    <Input readOnly value={watch("postalCode")} className="bg-muted/30 border-none h-10" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground">Maps Link</label>
                    <Input readOnly value={watch("mapsLink")} className="bg-muted/30 border-none h-10" />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold uppercase text-muted-foreground">Koordinat</label>
                  <div className="flex gap-2">
                    <Input readOnly value={watch("gpsLatitude")} className="bg-muted/30 border-none h-10" />
                    <Input readOnly value={watch("gpsLongitude")} className="bg-muted/30 border-none h-10" />
                  </div>
                </div>
                <div className="md:col-span-2 flex items-center gap-2 text-xs text-primary font-medium">
                  <Info className="size-3" />
                  Lokasi akurat membantu redaksi verifikasi
                </div>
              </div>}
          </CardContent>
        </Card>

        <section className="space-y-4">
          <h2 className="text-xl font-bold text-foreground">1. Pilih Kanal Berita</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[{
            id: "umum",
            label: "Berita Umum ABADI.NEWS",
            desc: "Berita nasional, regional, dan umum"
          }, {
            id: "ojol",
            label: "Berita POJOK OJOL",
            desc: "Khusus informasi seputar ojek online"
          }].map((opt, i) => <div key={opt.id} data-index={i} onClick={() => setValue("channel", opt.id)} className={cn("cursor-pointer p-5 rounded-lg border transition-all bg-white shadow-sm hover:shadow-md", channel === opt.id ? "border-primary ring-1 ring-primary" : "border-[#EEEEEE]")}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={cn("size-5 rounded-lg border-2 flex items-center justify-center", channel === opt.id ? "border-primary bg-primary" : "border-muted")}>
                    {channel === opt.id && <Check className="size-3 text-white" />}
                  </div>
                  <span className="font-bold text-base">{opt.label}</span>
                </div>
                <p className="text-sm text-muted-foreground">{opt.desc}</p>
              </div>)}
          </div>
        </section>

        <section className="space-y-6">
          <h2 className="text-xl font-bold text-foreground">2. 5W+1H ANALYSIS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="what" rules={{
            required: "Apa yang terjadi wajib diisi"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">WHAT (Apa yang terjadi?)</FormLabel>
                  <FormControl>
                    <Input placeholder="Contoh: Kebakaran hebat di pasar induk" className={inputClassName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="who" rules={{
            required: "Siapa yang terlibat wajib diisi"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">WHO (Siapa yang terlibat?)</FormLabel>
                  <FormControl>
                    <Input placeholder="Contoh: Pedagang pasar dan petugas damkar" className={inputClassName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="where" rules={{
            required: "Lokasi kejadian wajib diisi"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">WHERE (Di mana kejadiannya?)</FormLabel>
                  <FormControl>
                    <Input placeholder="Lokasi detail kejadian" className={inputClassName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="when" rules={{
            required: "Waktu kejadian wajib diisi"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">WHEN (Kapan kejadiannya?)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder="Pilih tanggal kejadian" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DATE_OPTIONS.map((opt, i) => <SelectItem key={i} value={opt.value} data-index={i}>
                          {opt.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="why" rules={{
            required: "Penyebab kejadian wajib diisi"
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-foreground text-sm font-bold">WHY (Mengapa hal itu terjadi?)</FormLabel>
                  <FormControl>
                    <Input placeholder="Contoh: Diduga akibat arus pendek listrik" className={inputClassName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="how" rules={{
            required: "Kronologi kejadian wajib diisi",
            minLength: {
              value: 300,
              message: "Minimal 300 karakter untuk kronologi yang lengkap"
            }
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-foreground text-sm font-bold">HOW (Bagaimana kronologinya?)</FormLabel>
                  <FormControl>
                    <textarea className="w-full min-h-[200px] p-4 border-[#EEEEEE] rounded-lg focus:ring-0 focus:border-[#FF6B00] text-base bg-white outline-none" placeholder="Ceritakan secara detail urutan kejadiannya (Minimal 300 karakter)..." {...field} />
                  </FormControl>
                  <FormDescription>Karakter saat ini: {field.value?.length || 0}/300</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="p-6 bg-accent/30 rounded-lg border border-[#EEEEEE] space-y-4">
            <h3 className="font-bold text-sm uppercase text-muted-foreground">Informasi Narasumber</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField control={control} name="narasumberName" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold">Nama Narasumber</FormLabel>
                    <FormControl>
                      <Input placeholder="Nama saksi/narasumber" className="h-10" {...field} />
                    </FormControl>
                  </FormItem>} />
              <FormField control={control} name="narasumberPhone" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-xs font-bold">Nomor Narasumber</FormLabel>
                    <FormControl>
                      <Input placeholder="0812..." className="h-10" {...field} />
                    </FormControl>
                  </FormItem>} />
              <FormField control={control} name="isSelfNarasumber" render={({
              field
            }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-2">
                    <FormControl>
                      <input type="checkbox" className="size-4 rounded border-gray-300 text-primary focus:ring-primary" checked={field.value} onChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-medium">Saya narasumber</FormLabel>
                    </div>
                  </FormItem>} />
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-lg font-semibold">WILAYAH KEJADIAN</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField control={control} name="wilayah" rules={{
            required: "Wilayah wajib dipilih"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">Wilayah / Zona</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder="Pilih Wilayah" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ZONA_DATA.map((zona, i) => <SelectItem key={zona} value={zona} data-index={i}>
                          {zona}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="kecamatan" rules={{
            required: "Kecamatan wajib dipilih"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">Kecamatan</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={!selectedWilayah}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder={selectedWilayah ? "Pilih Kecamatan" : "Pilih Zona terlebih dahulu"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {kecamatanOptions.map((opt, i) => <SelectItem key={opt.id} value={opt.name} data-index={i}>
                          {opt.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="village" rules={{
            required: "Desa/Kelurahan wajib diisi"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">Desa / Kelurahan</FormLabel>
                  <FormControl>
                    <Input placeholder="Isi nama desa atau kelurahan" className={inputClassName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-bold text-foreground">3. KATEGORI INFORMASI</h2>
          <FormField control={control} name="category" rules={{
          required: "Kategori informasi wajib dipilih"
        }} render={({
          field
        }) => <FormItem>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputClassName}>
                      <SelectValue placeholder="Pilih kategori berita..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {NEWS_CATEGORIES.map((cat, i) => <SelectItem key={cat} value={cat} data-index={i}>
                        {cat}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-bold text-foreground">4. TINGKAT PRIORITAS</h2>
          <FormField control={control} name="priority" rules={{
          required: "Tingkat prioritas wajib dipilih"
        }} render={({
          field
        }) => <FormItem>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[{
              id: "urgent",
              label: " Sangat Mendesak",
              desc: "Bencana alam, kecelakaan maut, kriminalitas aktif",
              color: "border-red-500 bg-red-50"
            }, {
              id: "important",
              label: " Penting",
              desc: "Kegiatan publik, kebijakan baru, info kemacetan",
              color: "border-orange-500 bg-orange-50"
            }, {
              id: "normal",
              label: " Normal",
              desc: "Berita umum, gaya hidup, kuliner, hiburan",
              color: "border-green-500 bg-green-50"
            }].map((opt, i) => <div key={opt.id} data-index={i} onClick={() => field.onChange(opt.id)} className={cn("cursor-pointer p-4 rounded-lg border-2 transition-all", field.value === opt.id ? opt.color : "border-[#EEEEEE] bg-white")}>
                      <p className="font-bold text-sm mb-1">{opt.label}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{opt.desc}</p>
                    </div>)}
                </div>
                <FormMessage />
              </FormItem>} />
        </section>

        <section className="space-y-6">
          <h2 className="text-xl font-bold text-foreground">5. Data Pengirim</h2>
          <div className="space-y-6">
            <FormField control={control} name="senderStatus" rules={{
            required: "Status pengirim wajib dipilih"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground text-sm font-bold">Status Pengirim</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder="Pilih status Anda..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SENDER_STATUS_OPTIONS.map((opt, i) => <SelectItem key={opt} value={opt} data-index={i}>
                          {opt}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            {senderStatus === "Anggota PP" && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-secondary/5 rounded-lg border border-secondary/20 animate-in zoom-in-95">
                <FormField control={control} name="ktaNumber" rules={{
              required: "Nomor KTA wajib diisi"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-xs font-bold">Nomor KTA</FormLabel>
                      <FormControl><Input placeholder="KTA-XXXX" className="h-10" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="membershipLevel" rules={{
              required: "Jenjang keanggotaan wajib diisi"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-xs font-bold">Jenjang Keanggotaan</FormLabel>
                      <FormControl><Input placeholder="Contoh: MPC/PAC" className="h-10" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="pacName" rules={{
              required: "Nama PAC wajib diisi"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-xs font-bold">PAC (Kecamatan)</FormLabel>
                      <FormControl><Input placeholder="Nama PAC" className="h-10" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {senderStatus === "Ojol" && <div className="p-6 bg-green-50 rounded-lg border border-green-200 animate-in zoom-in-95">
                <FormField control={control} name="ojolCommunity" rules={{
              required: "Nama komunitas ojol wajib diisi"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-xs font-bold">Komunitas Ojol</FormLabel>
                      <FormControl><Input placeholder="Nama Komunitas / Basecamp" className="h-10" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="senderName" rules={{
              required: "Nama lengkap wajib diisi"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground text-sm font-bold">Nama Lengkap</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input placeholder="Nama Anda" className={cn(inputClassName, "pl-12")} {...field} />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-5" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="senderPhone" rules={{
              required: "Nomor WhatsApp wajib diisi",
              pattern: {
                value: /^[0-9+\-\s]+$/,
                message: "Format nomor tidak valid"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground text-sm font-bold">Nomor WhatsApp</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input type="tel" placeholder="0812..." className={cn(inputClassName, "pl-12")} {...field} />
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-5" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="senderAddress" rules={{
              required: "Alamat detail wajib diisi"
            }} render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="text-foreground text-sm font-bold">Alamat Detail Pengirim</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input placeholder="Alamat lengkap Anda saat ini" className={cn(inputClassName, "pl-12")} {...field} />
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-5" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="senderEmail" rules={{
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Email tidak valid"
              }
            }} render={({
              field
            }) => <FormItem className="md:col-span-2">
                    <FormLabel className="text-foreground text-sm font-bold">Email (Opsional)</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input type="email" placeholder="email@anda.com" className={cn(inputClassName, "pl-12")} {...field} />
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground size-5" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <FormField control={control} name="allAgreementsSigned" rules={{
          required: "Anda harus menyetujui pedoman media siber"
        }} render={({
          field
        }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-muted/30 rounded-lg">
                <FormControl>
                  <input type="checkbox" className="size-4 mt-1 rounded border-gray-300 text-primary focus:ring-primary" checked={field.value} onChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-medium">
                    Saya menyatakan bahwa data yang diberikan adalah benar dan dapat dipertanggungjawabkan sesuai Pedoman Media Siber.
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>} />
        </section>

        <div className="pt-8 border-t border-[#EEEEEE] flex flex-col items-center gap-6">
          {validation.progress < 100 && <div className="w-full max-w-md p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-sm">
                <AlertCircle className="size-4" />
                Lengkapi Form ({validation.progress}%)
              </div>
              <div className="w-full bg-amber-200 h-2 rounded-lg overflow-hidden">
                <div className="bg-amber-500 h-full transition-all duration-500" style={{
              width: `${validation.progress}%`
            }} />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-amber-700 font-medium">Item yang masih kurang:</p>
                <ul className="text-[11px] text-amber-600 list-disc list-inside grid grid-cols-1 gap-x-4">
                  {validation.missingItems.slice(0, 5).map((item, idx) => <li key={idx}>{item}</li>)}
                  {validation.missingItems.length > 5 && <li className="list-none font-bold mt-1">+ {validation.missingItems.length - 5} item lainnya</li>}
                </ul>
              </div>
            </div>}
          
          <Button type="submit" disabled={isSubmitting || validation.progress < 100} className="h-[60px] px-12 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transition-all">
            {isSubmitting ? <><Loader2 className="mr-2 size-5 animate-spin" /> MENGIRIM...</> : <><Send className="mr-2 size-5" /> KIRIM INFORMASI SEKARANG</>}
          </Button>
          <p className="text-muted-foreground text-xs text-center max-w-md">
            Dengan mengirimkan informasi ini, Anda menyatakan bahwa data yang diberikan adalah benar dan dapat dipertanggungjawabkan sesuai Pedoman Media Siber.
          </p>
        </div>
      </div>
    </Form>;
}
export default function InformasiKejadianForm() {
  return <section id="informasikejadianform" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="bg-white p-5 shadow-lg border-border/50 rounded-lg">
          <CardHeader className="pb-8 border-b border-border/50 mb-8">
            <CardTitle className="text-2xl font-default font-bold !underline md:text-3xl text-primary">
              KIRIM INFORMASI ABADI.NEWS
            </CardTitle>
            <p className="text-muted-foreground mt-2">
              Kontribusikan informasi kejadian di sekitar Anda untuk dipublikasikan di ABADI.NEWS.
            </p>
          </CardHeader>
          <CardContent>
            <FormProvider formId="informasi-kejadian-form-v3" sectionName="Informasi Kejadian" defaultValues={{
            senderStatus: "Masyarakat Umum",
            gpsActive: false,
            gpsLatitude: "",
            gpsLongitude: "",
            address: "",
            wilayah: "",
            kecamatan: "",
            village: "",
            district: "",
            city: "",
            province: "",
            postalCode: "",
            mapsLink: "",
            channel: "umum",
            what: "",
            who: "",
            where: "",
            when: "",
            why: "",
            how: "",
            narasumberName: "",
            narasumberPhone: "",
            isSelfNarasumber: false,
            category: "",
            priority: "normal",
            senderName: "",
            senderEmail: "",
            senderPhone: "",
            senderAddress: "",
            ktaNumber: "",
            membershipLevel: "",
            pacName: "",
            ojolCommunity: "",
            allAgreementsSigned: false,
            photos: [],
            videos: [],
            documents: []
          }} submitText="Kirim Informasi" successMessage="Informasi kejadian berhasil dikirim. Tim redaksi ABADI.NEWS akan segera meninjau laporan Anda.">
              <InnerForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}